// src/components/admin/ProductRestockDialog.jsx
import React, { useState } from 'react';
import { X, Upload, Download, CheckCircle2, AlertCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { useApp } from '../../context/AppContext';
import { productApi } from '../../api';

const ProductRestockDialog = ({ onClose, onImported }) => {
  const { language } = useApp();
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState([]);
  const [parseError, setParseError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const T = {
    title: language === 'en' ? 'Batch Restock' : language === 'km' ? 'បញ្ចូលស្តុកច្រើន' : '批量进货',
    downloadTemplate: language === 'en' ? 'Download Template' : language === 'km' ? 'ទាញយកគំរូ' : '下载模板',
    choosePlaceholder: language === 'en' ? 'Click to choose an Excel file (.xlsx)' : language === 'km' ? 'ចុចដើម្បីជ្រើសរើសឯកសារ Excel (.xlsx)' : '点击选择Excel文件（.xlsx）',
    previewLabel: (n) => language === 'en' ? `Preview (${n} rows) — check before confirming`
      : language === 'km' ? `មើលជាមុន (${n} ជួរ) — សូមពិនិត្យមុនបញ្ជាក់`
      : `预览（共 ${n} 行，确认无误再点下方"确认导入"）`,
    colBarcode: language === 'en' ? 'Barcode' : language === 'km' ? 'បាកូដ' : '条码',
    colName: language === 'en' ? 'Product Name' : language === 'km' ? 'ឈ្មោះទំនិញ' : '商品名称',
    colQty: language === 'en' ? 'Quantity' : language === 'km' ? 'ចំនួន' : '进货数量',
    colCost: language === 'en' ? 'Cost Price' : language === 'km' ? 'តម្លៃដើម' : '本次成本价',
    none: language === 'en' ? '(none)' : language === 'km' ? '(គ្មាន)' : '（无）',
    statCreated: language === 'en' ? 'Restocked' : language === 'km' ? 'បានបញ្ចូល' : '成功',
    statFailed: language === 'en' ? 'Failed' : language === 'km' ? 'បរាជ័យ' : '失败',
    failedDetail: language === 'en' ? 'Failure details' : language === 'km' ? 'ព័ត៌មានលម្អិតបញ្ហា' : '失败详情',
    createdList: language === 'en' ? 'Restocked products' : language === 'km' ? 'ទំនិញដែលបានបញ្ចូលស្តុក' : '已补充库存的商品',
    cancel: language === 'en' ? 'Cancel' : language === 'km' ? 'បោះបង់' : '取消',
    confirmImport: (n) => language === 'en' ? `Confirm (${n})` : language === 'km' ? `បញ្ជាក់ (${n})` : `确认导入（${n}条）`,
    importing: language === 'en' ? 'Processing...' : language === 'km' ? 'កំពុងដំណើរការ...' : '处理中...',
    done: language === 'en' ? 'Done' : language === 'km' ? 'រួចរាល់' : '完成',
    noDataError: language === 'en'
      ? 'No importable data found — check that example rows were removed and real data starts from row 3.'
      : language === 'km'
      ? 'រកមិនឃើញទិន្នន័យសម្រាប់នាំចូល — សូមពិនិត្យថាបានលុបជួរឧទាហរណ៍ ហើយទិន្នន័យពិតចាប់ផ្តើមពីជួរទី៣។'
      : '没有读到可导入的数据，检查一下是不是把示例行也删了、数据没有从第3行开始填。',
    parseError: (msg) => language === 'en' ? `File parsing failed, make sure it's a standard .xlsx file: ${msg}`
      : language === 'km' ? `ការញែកឯកសារបរាជ័យ សូមប្រាកដថាជាឯកសារ .xlsx ស្តង់ដារ៖ ${msg}`
      : '文件解析失败，确认一下是不是标准的.xlsx文件：' + msg,
  };

  // 生成模板文件——表头 + 一行示例
  const downloadTemplate = () => {
    const headers = ['条码', '商品名称', '进货数量', '本次成本价'];
    const example = ['示例：6901234567890（有条码优先用条码，没有条码这两个都留空条码即可）', '示例：可乐330ml', 24, 0.28];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'restock');
    XLSX.writeFile(wb, '批量进货模板.xlsx');
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setParseError('');
    setResult(null);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        // 第1行表头，第2行示例行，第3行开始是真实数据
        const dataRows = raw.slice(2)
          .filter(row => row.some(cell => String(cell).trim() !== ''))
          .map(row => ({
            barcode: String(row[0] || '').trim(),
            productName: String(row[1] || '').trim(),
            quantity: row[2],
            cost_price: row[3],
          }));
        if (dataRows.length === 0) {
          setParseError(T.noDataError);
          return;
        }
        setRows(dataRows);
      } catch (err) {
        setParseError(T.parseError(err.message));
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    if (rows.length === 0) return;
    setSubmitting(true);
    try {
      const res = await productApi.bulkRestock({ items: rows });
      if (res.success) {
        setResult(res.data);
        onImported();
      } else {
        setParseError(res.message || T.parseError(''));
      }
    } catch (err) {
      setParseError(err.message || T.parseError(''));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="border-b p-6 flex justify-between items-center flex-shrink-0">
          <h3 className="text-xl font-bold">{T.title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {!result && (
            <button onClick={downloadTemplate}
              className="w-full flex items-center justify-center gap-2 border border-blue-200 bg-blue-50 text-blue-600 rounded-lg py-2.5 text-sm font-medium hover:bg-blue-100 transition-colors">
              <Download className="w-4 h-4" />
              {T.downloadTemplate}
            </button>
          )}

          {!result && (
            <div>
              <label className="flex items-center justify-center gap-2 border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">{fileName || T.choosePlaceholder}</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
              </label>
              {parseError && <p className="text-sm text-red-600 mt-2">{parseError}</p>}
            </div>
          )}

          {rows.length > 0 && !result && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">{T.previewLabel(rows.length)}</p>
              <div className="border rounded-lg overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colBarcode}</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600">{T.colName}</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">{T.colQty}</th>
                      <th className="px-3 py-2 text-right font-medium text-gray-600">{T.colCost}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td className="px-3 py-2 text-gray-500">{r.barcode || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2">{r.productName || <span className="text-gray-300">{T.none}</span>}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{r.quantity}</td>
                        <td className="px-3 py-2 text-right text-gray-500">${r.cost_price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {result && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-green-600">{result.created.length}</p>
                  <p className="text-xs text-green-700 mt-1">{T.statCreated}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{result.failed.length}</p>
                  <p className="text-xs text-red-700 mt-1">{T.statFailed}</p>
                </div>
              </div>

              {result.failed.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />{T.failedDetail}
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.failed.map((f, i) => (
                      <div key={i} className="text-xs bg-red-50 rounded px-3 py-2 text-red-700">
                        「{f.barcode || f.productName || '(未填)'}」：{f.reasons.join('；')}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.created.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />{T.createdList}
                  </p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {result.created.map((c, i) => (
                      <div key={i} className="text-xs bg-green-50 rounded px-3 py-2 text-green-700 flex justify-between">
                        <span>{c.name}</span>
                        <span>+{c.quantity} @ ${c.cost_price.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="border-t p-6 flex justify-end space-x-3 flex-shrink-0">
          {!result ? (
            <>
              <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                {T.cancel}
              </button>
              <button
                onClick={handleSubmit}
                disabled={rows.length === 0 || submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? T.importing : T.confirmImport(rows.length)}
              </button>
            </>
          ) : (
            <button onClick={onClose} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              {T.done}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductRestockDialog;